import { css } from '@emotion/css';
import { Colors } from '../../../shared/styles/colors';
import { PowerTypeEnum, SourceTypeEnum } from '../../../shared/types/enums';
import {
  Account,
  Socket,
  Source,
  SourceStatusEnum,
  User,
} from '../../../shared/types/server-types';
import { Motion, Radius, unstyledButton } from '../../../shared/styles/tokens';

export type CardStyle = {
  borderStyle: string;
  backgroundStyle: string;
  colorStyle: string;
  svgStyle: string;
};

//TODO: find a way to globally determine account in use
export const determineIsUsedByCurrentUser = (
  source: Source,
  user: User,
): boolean => {
  if (source.activeSession === null) {
    return false;
  }
  const usedByAccount = source.activeSession.group.account;

  return user.accounts.some(
    (account: Account) => account.id === usedByAccount.id,
  );
};

/**
 * Colour language of a source card (kept from the original design):
 *  - mono-phase sockets are blue, three-phase red, water is brand blue;
 *  - a green icon means "available";
 *  - a tinted card in the source colour means "in use by you";
 *  - purple means "plugged in, waiting";
 *  - gray means unavailable (used by someone else / out of service).
 */
export const determineCardStyle = (source: Source, User: User): CardStyle => {
  const isUsedByCurrentUser = determineIsUsedByCurrentUser(source, User);

  const unavailable: CardStyle = {
    backgroundStyle: Colors.SURFACE,
    borderStyle: Colors.BORDER,
    colorStyle: Colors.WINDOW_GRAY,
    svgStyle: Colors.WINDOW_GRAY,
  };

  const available = (accent: string): CardStyle => ({
    backgroundStyle: Colors.WHITE,
    borderStyle: accent,
    colorStyle: Colors.BLACK,
    svgStyle: Colors.SOURCE_AVAILABLE,
  });

  const inUse = (accent: string, tint: string): CardStyle =>
    isUsedByCurrentUser
      ? {
          backgroundStyle: tint,
          borderStyle: accent,
          colorStyle: Colors.BLACK,
          svgStyle: accent,
        }
      : unavailable;

  const pluggedIn: CardStyle = {
    backgroundStyle: Colors.PURPLE_TINT,
    borderStyle: Colors.PURPLE,
    colorStyle: Colors.PURPLE,
    svgStyle: Colors.PURPLE,
  };

  const sourceStyle: Record<string, CardStyle> = {
    // VALVE
    [`${SourceTypeEnum.VALVE}${SourceStatusEnum.AVAILABLE}`]: {
      ...available(Colors.CAPRI_BLUE),
      colorStyle: Colors.CAPRI_BLUE,
    },
    [`${SourceTypeEnum.VALVE}${SourceStatusEnum.IN_USE}`]: inUse(
      Colors.CAPRI_BLUE,
      Colors.CAPRI_BLUE_TINT,
    ),
    [`${SourceTypeEnum.VALVE}${SourceStatusEnum.OUT_OF_SERVICE}`]: unavailable,
    // SOCKET SINGLE
    [`${SourceTypeEnum.SOCKET}${PowerTypeEnum.SINGLE}${SourceStatusEnum.AVAILABLE}`]:
      available(Colors.MONO_PHASE),
    [`${SourceTypeEnum.SOCKET}${PowerTypeEnum.SINGLE}${SourceStatusEnum.IN_USE}`]:
      inUse(Colors.MONO_PHASE, Colors.MONO_PHASE_TINT),
    [`${SourceTypeEnum.SOCKET}${PowerTypeEnum.SINGLE}${SourceStatusEnum.PLUGGED_IN}`]:
      pluggedIn,
    [`${SourceTypeEnum.SOCKET}${PowerTypeEnum.SINGLE}${SourceStatusEnum.OUT_OF_SERVICE}`]:
      unavailable,
    // SOCKET TRIPLE
    [`${SourceTypeEnum.SOCKET}${PowerTypeEnum.TRIPLE}${SourceStatusEnum.AVAILABLE}`]:
      available(Colors.THREE_PHASE),
    [`${SourceTypeEnum.SOCKET}${PowerTypeEnum.TRIPLE}${SourceStatusEnum.IN_USE}`]:
      inUse(Colors.THREE_PHASE, Colors.THREE_PHASE_TINT),
    [`${SourceTypeEnum.SOCKET}${PowerTypeEnum.TRIPLE}${SourceStatusEnum.PLUGGED_IN}`]:
      pluggedIn,
    [`${SourceTypeEnum.SOCKET}${PowerTypeEnum.TRIPLE}${SourceStatusEnum.OUT_OF_SERVICE}`]:
      unavailable,
  };

  const phase = (source as Socket).phase ?? '';
  const styleKey = `${source.type}${phase}${source.status}`;

  return sourceStyle[styleKey] ?? unavailable;
};

export const reactiveCardStyle = (cardStyle: CardStyle): string => css`
  border: 2px solid ${cardStyle.borderStyle};
  background-color: ${cardStyle.backgroundStyle};
  color: ${cardStyle.colorStyle};

  figure {
    h4 {
      color: ${cardStyle.colorStyle};
    }

    svg {
      path {
        fill: ${cardStyle.svgStyle};
      }
    }
  }
`;

export const sourceCardStyle = css`
  ${unstyledButton}
  min-width: 0;
  min-height: 4.5rem;
  width: 100%;
  cursor: pointer;
  border: 2px solid ${Colors.MONO_PHASE};
  border-radius: ${Radius.md};
  transition:
    background-color ${Motion.base},
    border-color ${Motion.base},
    color ${Motion.base},
    transform ${Motion.fast},
    box-shadow ${Motion.fast};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 0.875rem;
  text-align: left;

  figure {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    padding: 0px;

    h4 {
      text-transform: capitalize;
      transition: color ${Motion.base};
      min-width: 0;
    }

    svg {
      flex: 0 0 auto;
      path {
        transition: fill ${Motion.base};
      }
    }
  }

  label {
    cursor: pointer;
    color: ${Colors.TEXT_SECONDARY};
    margin-top: 0.25rem;
  }

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 6px 16px -10px rgba(16, 42, 67, 0.35);
    }
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: 2px;
  }
`;
